// Migrated onto the house MenuPanel chassis (DESIGN_LANGUAGE.md §7.7, Phase
// C.2): MenuPanel shell + MenuPanelItem rows across all four nav groups.
// Deleted with the re-chassis: the four `margin: '- -Z2'` list pulls (they
// compensated the old hand-rolled `padding: 'Z1 A'` body — MenuPanel pads Y
// and MenuPanelItem rows run full-width, so a -Z2 pull would overflow the
// panel by Z2 − Y and clip), the per-row `':hover': { '& svg': … }` opacity
// armor plus the `background/border/cursor` resets (all fought the retired
// KeyValueColumnFields/DropdownItem chassis — MenuPanelItem is already a
// borderless transparent button row with the house `hover` wash). Rows that
// are links keep their own `extends` + textDecoration guards untouched.
const Dropdownlogo = {
  extends: 'MenuPanel',
  gap: 'A',
  CaptionTitle: {
    // Align captions (and the identity header below) with MenuPanelItem's
    // Z1 label inset — same idiom as MarketplaceFilters' MenuLabel.
    padding: '- Z1',
    Text: {
      text: '{{ editor.logoMenu.activeProject | polyglot }}'
    },
    if: (el) => el.isCanvas()
  },
  Flex: {
    if: (el) => el.isCanvas(),
    gap: 'Z',
    padding: '- Z1',
    Avatar: {
      props: (el) => ({
        avatarType: 'initials',
        boxSize: 'B+X1',
        src:
          el.call('getProjectIcon') ||
          `https://avatars.symbo.ls/${el.avatarType || 'initials'}/svg?seed=${
            el.key || 'no-avatar'
          }`,
        round: 'radiusControl'
      })
    },
    Hgroup: {
      flow: 'y',
      margin: '- Z - -',
      gap: 'W',
      H: {
        color: 'title',
        tag: 'span',
        overflow: 'hidden',
        whiteSpace: 'wrap',
        textOverflow: 'anywhere',
        text: (el) => el.getRootState('projectName')
      },
      P: {
        margin: '0',
        fontWeight: '400',
        color: 'paragraph',
        overflow: 'hidden',
        overflowWrap: 'anywhere',
        whiteSpace: 'wrap',
        textOverflow: 'anywhere',
        lineBreak: 'anywhere',
        text: (el) =>
          `${el.getRootState('workingProjectOwner')}/${el.getRootState('workingProjectKey')}`
      }
    },
    Upgrade: {
      if: (el) => el.isAuthorised() && el.getRootState('tier') === 'starter',
      extends: 'Link',
      color: 'blue',
      text: '{{ editor.logoMenu.freePlan | polyglot }}',
      margin: '- - - auto',
      alignSelf: 'start',
      whiteSpace: 'nowrap',
      fontSize: 'Z2',
      lineHeight: 1,
      ':hover': {
        textDecoration: 'underline'
      },
      onClick: (f, el, st) => {
        el.call('openRouterModal', '/settings', {
          key: '/pricing'
        })
      }
    }
  },
  ProjectNav: {
    if: (el) => !el.isDashboard(),
    CaptionTitle: {
      margin: 'Z - X',
      padding: '- Z1',
      Text: {
        text: '{{ editor.logoMenu.project | polyglot }}'
      }
    },
    Items: {
      flow: 'y',
      gap: '0',
      childExtends: 'MenuPanelItem',
      childProps: {
        Icon: {
          order: '-1',
          margin: '- Y1 - -'
        }
      },
      childrenAs: 'props',
      children: [
        {
          Icon: {
            name: 'upgrade'
          },
          text: '{{ editor.logoMenu.upgradeTeam | polyglot }}',
          Sup: {
            color: 'disabled',
            margin: '-Z - - X2',
            fontSize: 'Y',
            text: '{{ editor.logoMenu.soon | polyglot }}'
          }
        },
        {
          Icon: {
            name: 'billing'
          },
          text: '{{ settings.tabs.billing | polyglot }}'
        },
        {
          Icon: {
            name: 'sf settings fill'
          },
          text: '{{ editor.logoMenu.projectSettings | polyglot }}'
        }
      ]
    }
  },
  DashboardNav: {
    CaptionTitle: {
      margin: 'X -',
      padding: '- Z1',
      Text: {
        text: '{{ editor.dashboard.title | polyglot }}'
      }
    },
    Items: {
      flow: 'y',
      gap: '0',
      childExtends: 'MenuPanelItem',
      childProps: {
        Icon: {
          order: '-1',
          margin: '- Y1 - -'
        }
      },
      childrenAs: 'props',
      children: (el) => [
        {
          Icon: {
            name: 'plus oval'
          },
          text: '{{ editor.dashboard.newProject | polyglot }}',
          onClick: async (ev, el) => {
            await el.call('openRouterModal', '/settings', {
              key: '/onboarding'
            })
          }
        },
        {
          Icon: {
            name: 'arrows diagonal'
          },
          extends: ['Link', 'Button'],
          text: '{{ editor.logoMenu.switchProject | polyglot }}',
          // /studio is the project picker (the /dashboard route is retired)
          href: '/studio'
        },
        {
          Icon: {
            name: 'back'
          },
          extends: ['Link', 'Button'],
          text: '{{ editor.logoMenu.backToWorkspace | polyglot }}',
          href: '/'
        }
      ]
    }
  },
  UserNav: {
    CaptionTitle: {
      margin: 'Z - X',
      padding: '- Z1',
      Text: {
        text: '{{ editor.logoMenu.user | polyglot }}'
      }
    },
    Items: {
      flow: 'y',
      gap: '0',
      childExtends: 'MenuPanelItem',
      childProps: {
        Icon: {
          order: '-1',
          margin: '- Y1 - -'
        }
      },
      childrenAs: 'props',
      children: [
        {
          Icon: { name: 'user' },
          text: '{{ shell.accountMenu.profile | polyglot }}',
          onClick: async (ev, el) => {
            await el.call('openRouterModal', '/settings', {
              key: '/personal-account'
            })
          }
        },
        {
          extends: ['Link', 'Button'],
          Icon: { name: 'logout' },
          text: '{{ shell.accountMenu.signOut | polyglot }}',
          href: '/logout'
        }
      ]
    }
  },
  GeneralNav: {
    CaptionTitle: {
      margin: 'Z - -',
      padding: '- Z1',
      Text: {
        text: '{{ editor.logoMenu.general | polyglot }}'
      }
    },
    Items: {
      flow: 'y',
      gap: '0',
      childExtends: 'MenuPanelItem',
      childProps: {
        Icon: {
          order: '-1',
          margin: '- Y1 - -'
        }
      },
      childrenAs: 'props',
      children: (el) => [
        {
          extends: 'DocsLink',
          href: '/developers',
          text: '{{ editor.logoMenu.whatsNew | polyglot }}',
          ':hover': {
            textDecoration: 'none'
          },
          Icon: {
            name: 'news'
          }
        },
        {
          extends: 'DocsLink',
          href: '/developers',
          text: '{{ editor.logoMenu.about | polyglot }}',
          ':hover': {
            textDecoration: 'none'
          },
          Icon: {
            name: 'sf info'
          }
        },
        {
          extends: 'DocsLink',
          href: '/docs/resources',
          text: '{{ editor.logoMenu.support | polyglot }}',
          ':hover': {
            textDecoration: 'none'
          },
          Icon: {
            name: 'support'
          }
        }
      ]
    }
  }
}

export { Dropdownlogo as 'Dropdown.Logo' }
